import { addDays, nextMonday, startOfWeek, addWeeks, format, isToday, isTomorrow, isSameDay } from 'date-fns'
import { zhCN } from 'date-fns/locale'

import { cn } from '@/lib/utils'

export interface DateQuickOption {
  label: string
  value: string // ISO date string YYYY-MM-DD
  isSelected?: boolean
}

/**
 * 获取日期快捷选项
 * @param currentDate 当前选中的日期 (YYYY-MM-DD 格式)
 * @param options 配置选项
 * @returns 日期快捷选项数组
 */
export function getDateQuickOptions(
  currentDate?: string,
  options?: {
    includeToday?: boolean
    includeTomorrow?: boolean
    includeNextMonday?: boolean
    includeNextWeekStart?: boolean
    includeThisWeekEnd?: boolean
    maxFutureDays?: number
  }
): DateQuickOption[] {
  const {
    includeToday = true,
    includeTomorrow = true,
    includeNextMonday = true,
    includeNextWeekStart = false,
    includeThisWeekEnd = false,
    maxFutureDays,
  } = options || {}

  const today = new Date()
  const tomorrow = addDays(today, 1)
  const nextMondayDate = nextMonday(today)
  const thisWeekEnd = addDays(startOfWeek(today, { weekStartsOn: 1 }), 4) // 周五
  const nextWeekStart = addWeeks(startOfWeek(today, { weekStartsOn: 1 }), 1) // 下周一

  const formatDate = (date: Date) => format(date, 'yyyy-MM-dd')
  const currentDateObj = currentDate ? new Date(currentDate) : null

  const quickOptions: DateQuickOption[] = []

  // 今天
  if (includeToday) {
    const todayStr = formatDate(today)
    quickOptions.push({
      label: '今天',
      value: todayStr,
      isSelected: currentDateObj ? isSameDay(currentDateObj, today) : false,
    })
  }

  // 明天
  if (includeTomorrow) {
    const tomorrowStr = formatDate(tomorrow)
    if (!maxFutureDays || 1 <= maxFutureDays) {
      quickOptions.push({
        label: '明天',
        value: tomorrowStr,
        isSelected: currentDateObj ? isSameDay(currentDateObj, tomorrow) : false,
      })
    }
  }

  // 本周五 (如果今天不是周五或之后)
  if (includeThisWeekEnd) {
    const dayOfWeek = today.getDay()
    if (dayOfWeek >= 1 && dayOfWeek < 5) {
      // 周一到周四
      const thisWeekEndStr = formatDate(thisWeekEnd)
      const daysUntilFriday = 5 - dayOfWeek
      if (!maxFutureDays || daysUntilFriday <= maxFutureDays) {
        quickOptions.push({
          label: '本周五',
          value: thisWeekEndStr,
          isSelected: currentDateObj ? isSameDay(currentDateObj, thisWeekEnd) : false,
        })
      }
    }
  }

  // 下周一
  if (includeNextMonday) {
    const nextMondayStr = formatDate(nextMondayDate)
    const daysUntilNextMonday = Math.ceil(
      (nextMondayDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (!maxFutureDays || daysUntilNextMonday <= maxFutureDays) {
      quickOptions.push({
        label: '下周一',
        value: nextMondayStr,
        isSelected: currentDateObj ? isSameDay(currentDateObj, nextMondayDate) : false,
      })
    }
  }

  // 下周开始 (如果与下周一不同)
  if (includeNextWeekStart && !includeNextMonday) {
    const nextWeekStartStr = formatDate(nextWeekStart)
    const daysUntilNextWeek = Math.ceil(
      (nextWeekStart.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)
    )
    if (!maxFutureDays || daysUntilNextWeek <= maxFutureDays) {
      quickOptions.push({
        label: '下周',
        value: nextWeekStartStr,
        isSelected: currentDateObj ? isSameDay(currentDateObj, nextWeekStart) : false,
      })
    }
  }

  return quickOptions
}

/**
 * 获取日期的友好显示名称
 * @param dateStr 日期字符串 (YYYY-MM-DD 格式)
 * @returns 友好的日期名称
 */
export function getDateFriendlyName(dateStr: string): string {
  if (!dateStr) {return ''}

  const date = new Date(dateStr)
  const today = new Date()

  if (isToday(date)) {
    return '今天'
  }

  if (isTomorrow(date)) {
    return '明天'
  }

  // 检查是否是下周一
  const nextMondayDate = nextMonday(today)
  if (isSameDay(date, nextMondayDate)) {
    return '下周一'
  }

  // 检查是否是本周五
  const dayOfWeek = today.getDay()
  if (dayOfWeek >= 1 && dayOfWeek < 5) {
    const thisWeekEnd = addDays(startOfWeek(today, { weekStartsOn: 1 }), 4)
    if (isSameDay(date, thisWeekEnd)) {
      return '本周五'
    }
  }

  // 返回格式化的日期
  return format(date, 'M月d日 EEEE', { locale: zhCN })
}

interface DateQuickSelectProps {
  value?: string
  onChange: (value: string) => void
  className?: string
  buttonClassName?: string
  options?: {
    includeToday?: boolean
    includeTomorrow?: boolean
    includeNextMonday?: boolean
    includeThisWeekEnd?: boolean
    maxFutureDays?: number
  }
  disabled?: boolean
}

/**
 * 日期快捷选择组件
 * 提供今天、明天、下周一等快捷按钮
 * Requirements: 11.6
 */
export function DateQuickSelect({
  value,
  onChange,
  className,
  buttonClassName,
  options,
  disabled = false,
}: DateQuickSelectProps) {
  const quickOptions = getDateQuickOptions(value, options)

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)}>
      {quickOptions.map((option) => (
        <button
          key={option.value}
          type="button"
          disabled={disabled}
          onClick={() => onChange(option.value)}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors',
            'hover:bg-accent hover:text-accent-foreground',
            'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1',
            'disabled:pointer-events-none disabled:opacity-50',
            option.isSelected
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-input bg-background text-muted-foreground',
            buttonClassName
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  )
}

interface DateInputWithQuickSelectProps {
  value: string
  onChange: (value: string) => void
  label?: string
  required?: boolean
  error?: string
  className?: string
  inputClassName?: string
  quickSelectClassName?: string
  quickSelectOptions?: {
    includeToday?: boolean
    includeTomorrow?: boolean
    includeNextMonday?: boolean
    includeThisWeekEnd?: boolean
    maxFutureDays?: number
  }
  disabled?: boolean
  min?: string
  max?: string
  showQuickSelect?: boolean
}

/**
 * 带快捷选择的日期输入组件
 * 结合原生日期输入和快捷选择按钮
 * Requirements: 11.6
 */
export function DateInputWithQuickSelect({
  value,
  onChange,
  label,
  required = false,
  error,
  className,
  inputClassName,
  quickSelectClassName,
  quickSelectOptions,
  disabled = false,
  min,
  max,
  showQuickSelect = true,
}: DateInputWithQuickSelectProps) {
  return (
    <div className={cn('space-y-2', className)}>
      {label && (
        <label className="block text-sm font-medium">
          {label}
          {required && <span className="text-destructive ml-0.5">*</span>}
        </label>
      )}

      <div className="space-y-2">
        <div className="relative">
          <input
            type="date"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            min={min}
            max={max}
            className={cn(
              'w-full rounded-md border bg-background px-3 py-2 text-sm',
              'focus:outline-none focus:ring-2 focus:ring-primary',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-destructive',
              inputClassName
            )}
          />
        </div>

        {showQuickSelect && (
          <DateQuickSelect
            value={value}
            onChange={onChange}
            options={quickSelectOptions}
            disabled={disabled}
            className={quickSelectClassName}
          />
        )}
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  )
}

export { DateQuickSelect as default }
